import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth';
import { FsStateConvertPipe } from '@lib/common/pipes';

import { LprExportTriggerRequest, ILprExportParamsInterface } from '../api/interfaces';

@Injectable({ providedIn: 'root' })
export class LprExportRequestFormatterService {
  private static readonly defaultRadiusInMiles: number = 25;
  private static readonly metersPerMile = 1609.344;
  private readonly _plateStateConvertPipe: FsStateConvertPipe = new FsStateConvertPipe();

  constructor(private readonly _currentOrganization: SnCurrentOrganization) {}

  /**
   * Converts search parameters to API request format
   * @param searchParams Current search parameters
   * @returns Formatted request data
   */
  public formatExportRequest(searchParams: ILprExportParamsInterface): LprExportTriggerRequest {
    const params = { ...searchParams } as ILprExportParamsInterface;

    const request: LprExportTriggerRequest = {
      reportFormat: 'csv',
      params: {
        ...params,
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate,
      },
    };

    if (searchParams.plateState) {
      request.params.plateState = this._plateStateConvertPipe.transform(
        searchParams.plateState,
        'short',
      );
    }

    if (searchParams.inBbox) {
      request.params.inBbox = searchParams.inBbox;
    } else {
      const defaultBbox = this.getDefaultBoundingBox();
      if (defaultBbox) {
        request.params.inBbox = { defaultFsBbox: defaultBbox };
      }
    }

    return request;
  }

  /**
   * Generates a default bounding box based on organization coordinates
   * Creates a circle with 25-mile radius around the org's location
   * @returns Bounding box string in format "lng1,lat1,lng2,lat2" or null if coordinates are invalid
   */
  public getDefaultBoundingBox(): string | null {
    try {
      const radius = Math.round(
        LprExportRequestFormatterService.defaultRadiusInMiles *
          LprExportRequestFormatterService.metersPerMile,
      );
      const point = this._currentOrganization.settings.geometry.coordinates.join();

      if (!point || !radius) {
        return null;
      }

      const points = point.split(',');
      if (points.length !== 2) {
        return null;
      }

      const circle = new google.maps.Circle({
        center: new google.maps.LatLng({
          lat: +points[1],
          lng: +points[0],
        }),
        radius,
      });

      const bounds = circle.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      return `${sw.lng()},${ne.lat()},${ne.lng()},${sw.lat()}`;
    } catch (error) {
      console.error('Failed to generate default bounding box:', error);
      return null;
    }
  }
}
